import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import chatService from '../../../services/chatService';
import { useI18n } from '../../../i18n/I18nContext';
import { ActionButtonsSkeleton } from '../TalkSkeleton';

export default function DeleteMessageModal({ message, onClose, onDeleted }) {
  const { t } = useI18n();
  const [options, setOptions] = useState(null);
  const [errors, setErrors] = useState([]);
  const [sending, setSending] = useState(false);
  const panel = useRef(null);
  const mounted = useRef(false);
  const inFlight = useRef(false);
  const callbacks = useRef({ onClose, onDeleted });
  callbacks.current = { onClose, onDeleted };
  const text = (key, fallback) => t(`talk.deletion.${key}`, {}, fallback);

  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    const previousFocus = document.activeElement;
    panel.current?.querySelector('button')?.focus();
    chatService.getDeletionOptions(message.id).then(result => {
      if (cancelled) return;
      if (result.status) setOptions(result.data.message);
      else setErrors([result.message || t('talk.deletion.optionsFailed', {}, 'Could not load message options. Please close and try again.')]);
    }).catch(() => {
      if (!cancelled) setErrors([t('talk.deletion.optionsFailed', {}, 'Could not load message options. Please close and try again.')]);
    });
    const keydown = event => {
      if (event.key === 'Escape' && !inFlight.current) callbacks.current.onClose();
      if (event.key === 'Tab') {
        const buttons = Array.from(panel.current?.querySelectorAll('button:not(:disabled)') || []);
        const first = buttons[0], last = buttons[buttons.length - 1];
        if ((event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first)?.focus();
        }
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      cancelled = true;
      mounted.current = false;
      document.removeEventListener('keydown', keydown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [message.id, t]);

  const submit = async scope => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSending(true);
    setErrors([]);
    try {
      const result = await chatService.deleteMessage(message.id, scope);
      if (!mounted.current) return;
      if (!result.status) {
        setErrors([result.message || text('failed', 'Could not delete this message. Please try again.')]);
        return;
      }
      callbacks.current.onDeleted(result.data);
      callbacks.current.onClose();
    } catch (error) {
      if (mounted.current) {
        setErrors(error.status === 422 ? Object.values(error.errors || {}).flat()
          : [text('failed', 'Could not delete this message. Please try again.')]);
        // Eligibility may have expired while the dialog was open.
        if (error.status === 403) setOptions(previous => ({ ...previous, can_delete_for_everyone: false }));
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setSending(false);
    }
  };

  return createPortal(
    <div className="talk-forward-layer" onClick={event => {
      if (event.target === event.currentTarget && !inFlight.current) onClose();
    }}>
      <div className="talk-forward-panel talk-delete-panel" ref={panel} role="dialog" aria-modal="true"
        aria-labelledby="talk-delete-title" aria-busy={sending}>
        <h2 id="talk-delete-title">{text('title', 'Delete message?')}</h2>
        <p>{text('description', 'Delete for me removes only your copy. Delete for everyone replaces the message for all participants.')}</p>
        {errors.length > 0 && <div role="alert" className="talk-action-error">{errors.map((error, index) => <p key={index}>{error}</p>)}</div>}
        <div className="talk-delete-actions">
          {!options && errors.length === 0 && <ActionButtonsSkeleton label={text('loading', 'Loading options…')} />}
          {options?.can_delete_for_everyone && <button type="button" className="talk-delete-danger" disabled={sending}
            onClick={() => submit('everyone')}>{text('everyone', 'Delete for everyone')}</button>}
          {options?.can_delete_for_me && <button type="button" className="talk-delete-danger" disabled={sending}
            onClick={() => submit('me')}>{text('me', 'Delete for me')}</button>}
          <button type="button" className="talk-delete-cancel" disabled={sending} onClick={onClose}>{text('cancel', 'Cancel')}</button>
        </div>
      </div>
    </div>, document.body
  );
}
